import { useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { LoadingView, Button } from "@solsys/solsys-reactjs";
import { FaFilePdf } from "react-icons/fa";
import { exportElementToPdf } from "./exportFsrPdf";
import { TICKET_EVENT_TYPE } from "../../eventTypes";
import "./fsr.css";

const reportQuery = gql`
  query fsrReport($fsr_list_request: fsr_list_request) {
    fsr(fsr_list_request: $fsr_list_request) {
      edges {
        id
        created_at
        crane {
          id
          name
          serial_no
        }
        ticket {
          edges {
            id
            name
            description
            operator
            status_id
            ticket_status {
              id
              name
            }
            created_at
            updated_at
            ticket_type {
              id
              name
            }
            ticket_event {
              edges {
                id
                message
                type_id
                ticket_event_type {
                  id
                  name
                }
                user_display_name
                created_at
                service_part {
                  part_no
                  quantity
                  description
                }
              }
            }
          }
        }
        service {
          edges {
            id
            created_at
            service_definition {
              name
            }
            user {
              display_name
            }
            service_value {
              edges {
                id
                status
                description
                service_item_id
                service_item {
                  id
                  name
                  service_group {
                    id
                    name
                  }
                }
              }
            }
            service_part {
              edges {
                id
                service_item_id
                quantity
                part_no
                description
                service_item {
                  id
                  name
                  service_group {
                    id
                    name
                  }
                  service_unit {
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

function formatDate(iso) {
    if (!iso) return "—";
    return DateTime.fromISO(iso, { zone: "utc" }).toLocal().toFormat("MMM d, yyyy h:mm a");
}

function formatDateShort(iso) {
    if (!iso) return null;
    return DateTime.fromISO(iso, { zone: "utc" }).toLocal().toFormat("MMM d, yyyy");
}

function eventLabel(event) {
    if (Number(event.type_id) === TICKET_EVENT_TYPE.PART && event.service_part) {
        const p = event.service_part;
        const qty = p.quantity ?? 1;
        return `${qty} part${qty > 1 ? "s" : ""}: ${p.part_no || p.description || "Part"}`;
    }
    return event.message || event.ticket_event_type?.name || "Event";
}

function isInspectionItem(serviceItem) {
    const groupId = serviceItem?.service_group?.id;
    if (groupId != null) return Number(groupId) === 1;
    return (serviceItem?.service_group?.name || "").toLowerCase() === "inspections";
}

function inspectionStatusLabel(status) {
    if (status === 1) return "Action";
    if (status === 2) return "OK";
    return null;
}

function sortedTicketEvents(ticket) {
    return [...(ticket.ticket_event?.edges ?? [])].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
}

function buildServiceRecordedRows(service) {
    const values = service.service_value?.edges ?? [];
    const parts = service.service_part?.edges ?? [];
    const rows = [];

    const serviceLevelValue = values.find((v) => {
        const itemId = v.service_item?.id ?? v.service_item_id;
        return itemId == null || Number(itemId) === 0;
    });
    if (serviceLevelValue?.description?.trim()) {
        rows.push({
            key: "service-comment",
            label: "Service comment",
            value: serviceLevelValue.description.trim(),
        });
    }

    const valuesByItemId = new Map();
    values.forEach((v) => {
        const itemId = v.service_item?.id ?? v.service_item_id;
        if (itemId != null && Number(itemId) !== 0) {
            valuesByItemId.set(Number(itemId), v);
        }
    });

    const partsByItemId = new Map();
    parts.forEach((p) => {
        const itemId = p.service_item?.id ?? p.service_item_id;
        if (itemId != null) partsByItemId.set(Number(itemId), p);
    });

    const allItemIds = new Set([...valuesByItemId.keys(), ...partsByItemId.keys()]);

    [...allItemIds].sort((a, b) => a - b).forEach((itemId) => {
        const sv = valuesByItemId.get(itemId);
        const sp = partsByItemId.get(itemId);
        const serviceItem = sv?.service_item ?? sp?.service_item;
        const name = serviceItem?.name || "Item";

        if (isInspectionItem(serviceItem)) {
            const pieces = [];
            const status = inspectionStatusLabel(sv?.status);
            if (status) pieces.push(status);
            if (sp?.part_no) pieces.push(`Part: ${sp.part_no}`);
            if (sp?.quantity != null && sp.quantity !== "") pieces.push(`Qty: ${sp.quantity}`);
            if (sv?.description?.trim()) pieces.push(sv.description.trim());
            rows.push({
                key: `inspection-${itemId}`,
                label: name,
                value: pieces.length ? pieces.join(" — ") : "—",
            });
        } else {
            const unit = serviceItem?.service_unit?.name || "Unit";
            const qty = sp?.quantity;
            rows.push({
                key: `collection-${itemId}`,
                label: name,
                value: qty != null && qty !== "" ? `${qty} ${unit}` : "—",
            });
        }
    });

    return rows;
}

function computeDateRange(tickets, services) {
    const dates = [];
    tickets.forEach((t) => {
        if (t.created_at) dates.push(new Date(t.created_at));
        if (t.updated_at) dates.push(new Date(t.updated_at));
        (t.ticket_event?.edges ?? []).forEach((e) => {
            if (e.created_at) dates.push(new Date(e.created_at));
        });
    });
    services.forEach((s) => {
        if (s.created_at) dates.push(new Date(s.created_at));
    });
    if (!dates.length) return null;
    dates.sort((a, b) => a - b);
    return {
        start: formatDateShort(dates[0].toISOString()),
        end: formatDateShort(dates[dates.length - 1].toISOString()),
    };
}

export default function FSRReport({ id }) {
    const reportRef = useRef(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    const { data, loading, error } = useQuery(reportQuery, {
        variables: {
            fsr_list_request: {
                filters: [{ field: "id", operator: "=", value: String(id) }],
                pagination: { limit: 1 },
            },
        },
        skip: !id,
        fetchPolicy: "network-only",
    });

    if (loading) return <LoadingView />;
    if (error) {
        return <div className="fsr-report-error">Failed to load FSR: {error.message}</div>;
    }

    const fsr = data?.fsr?.edges?.[0];
    if (!fsr) {
        return <div className="fsr-report-error">FSR not found.</div>;
    }

    const tickets = [...(fsr.ticket?.edges ?? [])].sort(
        (a, b) => Number(a.id) - Number(b.id)
    );
    const services = [...(fsr.service?.edges ?? [])].sort(
        (a, b) => Number(a.id) - Number(b.id)
    );

    const range = computeDateRange(tickets, services);

    const handleExportPdf = async () => {
        if (!reportRef.current || exporting) return;
        setExportError(null);
        setExporting(true);
        try {
            const craneName = (fsr.crane?.name || "E-Crane").replace(/[^\w\s-]/g, "").trim();
            const filename = `FSR-${fsr.id}-${craneName || "report"}.pdf`;
            await exportElementToPdf(reportRef.current, filename);
        } catch (err) {
            setExportError(err?.message || "Failed to export PDF");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="fsr-report">
            <div className="page-header no-print">
                <span className="page-header-title">FSR #{fsr.id}</span>
                <div className="page-header-options">
                    <Button onClick={handleExportPdf} disabled={exporting}>
                        <FaFilePdf /> {exporting ? "Exporting…" : "Export PDF"}
                    </Button>
                </div>
            </div>
            {exportError && (
                <div className="fsr-report-error no-print">{exportError}</div>
            )}

            <div ref={reportRef} className="fsr-report-content">
            <header className="fsr-report-header">
                <div className="fsr-report-title-row">
                    <h1>{fsr.crane?.name || "E-Crane"}</h1>
                    <span className="fsr-report-badge">FSR #{fsr.id}</span>
                </div>
                <div className="fsr-report-meta">
                    <div>
                        <strong>Serial:</strong> {fsr.crane?.serial_no || "—"}
                    </div>
                    <div>
                        <strong>Created:</strong> {formatDate(fsr.created_at)}
                    </div>
                </div>
                <div className="fsr-report-stats">
                    <div>
                        <strong>{tickets.length}</strong>
                        <span>Tickets</span>
                    </div>
                    <div>
                        <strong>{services.length}</strong>
                        <span>Services</span>
                    </div>
                    {range && (
                        <div>
                            <strong>{range.start} – {range.end}</strong>
                            <span>Date range</span>
                        </div>
                    )}
                </div>
            </header>

            {tickets.length > 0 && (
                <section className="fsr-report-section">
                    <h2>Tickets</h2>
                    {tickets.map((ticket) => {
                        const events = sortedTicketEvents(ticket);
                        return (
                        <article key={ticket.id} className="fsr-report-ticket">
                            <h3>
                                <span className="fsr-ticket-id">#{ticket.id}</span>
                                {ticket.name}
                            </h3>
                            {ticket.description && (
                                <p className="fsr-ticket-desc">
                                    <em>{ticket.description}</em>
                                    {ticket.operator && <> — {ticket.operator}</>}
                                </p>
                            )}
                            <table className="fsr-report-table">
                                <tbody>
                                    <tr>
                                        <th>Type</th>
                                        <td>{ticket.ticket_type?.name || "—"}</td>
                                        <th>Opened</th>
                                        <td>{formatDate(ticket.created_at)}</td>
                                    </tr>
                                    <tr>
                                        <th>Status</th>
                                        <td>{ticket.ticket_status?.name || (ticket.status_id === 2 ? "Closed" : "Open")}</td>
                                        <th>Closed</th>
                                        <td>{formatDate(ticket.updated_at)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            {events.length > 0 && (
                                <div className="fsr-event-timeline">
                                    <h4>Activity</h4>
                                    {events.map((event) => (
                                        <div key={event.id} className="fsr-event-row">
                                            <span className="fsr-event-meta">
                                                {formatDate(event.created_at)}
                                                {event.user_display_name
                                                    ? ` — ${event.user_display_name}`
                                                    : ""}
                                            </span>
                                            <p>{eventLabel(event)}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </article>
                        );
                    })}
                </section>
            )}

            {services.length > 0 && (
                <section className="fsr-report-section">
                    <h2>Services</h2>
                    {services.map((service) => {
                        const recordedRows = buildServiceRecordedRows(service);
                        return (
                        <article key={service.id} className="fsr-report-service">
                            <h3>
                                Service #{service.id}
                                {service.service_definition?.name
                                    ? ` — ${service.service_definition.name}`
                                    : ""}
                            </h3>
                            <table className="fsr-report-table">
                                <tbody>
                                    <tr>
                                        <th>Technician</th>
                                        <td>{service.user?.display_name || "—"}</td>
                                        <th>Submitted</th>
                                        <td>{formatDate(service.created_at)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            {recordedRows.length > 0 && (
                                <div className="fsr-service-values">
                                    <h4>Recorded values</h4>
                                    <ul>
                                        {recordedRows.map((row) => (
                                            <li key={row.key}>
                                                <strong>{row.label}:</strong> {row.value}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </article>
                        );
                    })}
                </section>
            )}

            {tickets.length === 0 && services.length === 0 && (
                <p className="fsr-report-empty">No tickets or services linked to this FSR.</p>
            )}
            </div>
        </div>
    );
}
